<script setup lang="ts">
import type { GlobalPrimitiveFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { LayoutSortingState } from '#layers/listing/types/layout';
import { computed } from 'vue';
import { SortDirection } from '#layers/base/public';
import { CollectionFieldSelect } from '#layers/data-model/public';
import { useSortGate } from '#layers/listing/composables/use-sort-gate';
import { SortFormField } from '#layers/ui/public';

const props = defineProps<{
	collection: ResolvedMonospaceCollection;
}>();

const sortGate = useSortGate(() => props.collection);

const modelValue = defineModel<LayoutSortingState>({ default: () => [] });

// SortingState is an array, but we only support single-column sorting here
const sort = computed({
	get: () => modelValue.value?.[0],
	set: (value) => {
		if (value) {
			modelValue.value = [value];
		}
		else {
			modelValue.value = [];
		}
	},
});

const sortField = computed({
	get: () => sort.value?.fieldId ?? null,
	set: (value) => {
		const directions = value ? sortGate.directions(value) : [];

		if (value && directions.length > 0) {
			sort.value = { fieldId: value, direction: directions.includes(SortDirection.Ascending) ? SortDirection.Ascending : directions[0]! };
		}
		else {
			sort.value = undefined;
		}
	},
});

const sortDirection = computed({
	get: () => sort.value?.direction ?? SortDirection.Ascending,
	set: (value) => {
		if (!sort.value) return;
		sort.value = { ...sort.value, direction: value };
	},
});
</script>

<template>
	<SortFormField
		v-model:direction="sortDirection"
		:show-toggle="!!sortField && sortGate.directions(sortField).length > 1"
	>
		<template #field-select>
			<CollectionFieldSelect
				v-model="sortField"
				include-primitive-fields
				:collection
				:include-relation-fields="false"
				:include-constrained-fields="false"
				:clear="false"
				:filter="(field) => sortGate.directions(field.id as GlobalPrimitiveFieldId).length > 0"
				class="w-full"
			/>
		</template>
	</SortFormField>
</template>
